import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';

import { GuardDailyItem } from './types';

const CARD_BACKGROUNDS = [
  'linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
  'linear-gradient(135deg, rgba(245,243,255,0.96) 0%, rgba(255,255,255,0.98) 100%)',
  'linear-gradient(135deg, rgba(240,253,250,0.95) 0%, rgba(255,255,255,0.98) 100%)',
  'linear-gradient(135deg, rgba(255,251,235,0.96) 0%, rgba(255,255,255,0.98) 100%)',
];

interface GuardMessageCardProps {
  item: GuardDailyItem;
  index: number;
}

export const GuardMessageCard: React.FC<GuardMessageCardProps> = ({ item, index }) => {
  const [expanded, setExpanded] = useState(false);
  const background = useMemo(() => CARD_BACKGROUNDS[index % CARD_BACKGROUNDS.length], [index]);
  const hasStructuredContent = item.summary_points.length > 0 || Boolean(item.core_content.trim());

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '24px',
        background,
        border: '1px solid rgba(147, 197, 253, 0.38)',
        boxShadow: '0 24px 40px rgba(37, 99, 235, 0.08)',
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', mb: 0.6 }}>
              {item.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={item.platform || 'unknown'}
                size="small"
                sx={{ bgcolor: 'rgba(37, 99, 235, 0.09)', color: '#1d4ed8', fontWeight: 700 }}
              />
              <Chip
                label={item.fetch_time || '--:--'}
                size="small"
                sx={{ bgcolor: 'rgba(15, 23, 42, 0.06)', color: '#475569', fontWeight: 700 }}
              />
            </Stack>
          </Box>
        </Box>

        {hasStructuredContent && (
          <Box>
            <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '0.83rem', mb: 0.75 }}>
              核心内容
            </Typography>
            {item.summary_points.length > 0 ? (
              <Box
                component="ol"
                sx={{
                  m: 0,
                  pl: 2.5,
                  color: '#0f172a',
                  lineHeight: 1.85,
                  fontSize: '0.96rem',
                }}
              >
                {item.summary_points.map((point) => (
                  <Box component="li" key={point} sx={{ mb: 0.9, '&:last-of-type': { mb: 0 } }}>
                    {point}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#0f172a', lineHeight: 1.8, fontSize: '0.96rem' }}>
                {item.core_content}
              </Typography>
            )}
          </Box>
        )}

        {item.keywords.length > 0 && (
          <Box>
            <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '0.83rem', mb: 0.75 }}>
              关键词
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {item.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.78)',
                    border: '1px solid rgba(191, 219, 254, 0.9)',
                    color: '#334155',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Box>
          <Button
            variant="text"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{ px: 0, minWidth: 'auto', color: '#2563eb', fontWeight: 700 }}
          >
            {expanded ? '收起原文' : '展开原文'}
          </Button>
          {expanded && (
            <Box
              sx={{
                mt: 1.25,
                p: 2,
                borderRadius: '18px',
                bgcolor: 'rgba(255,255,255,0.72)',
                border: '1px solid rgba(219, 234, 254, 0.95)',
              }}
            >
              <Typography sx={{ color: '#334155', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                {item.raw_transcript || '暂无原文'}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
